import React, { createContext, useContext, useState, useCallback } from 'react';
import { loadState, saveState, resetState, genId, buildNumber, seqOf, computeTotals } from '../lib/store';

const DataContext = createContext(null);

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const [state, setState] = useState(() => loadState());

  const persist = useCallback((updater) => {
    setState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveState(next);
      return next;
    });
  }, []);

  // Generic collection CRUD
  const addItem = useCallback((collection, item) => {
    persist((prev) => ({ ...prev, [collection]: [{ ...item, id: item.id || genId() }, ...prev[collection]] }));
  }, [persist]);

  const updateItem = useCallback((collection, id, patch) => {
    persist((prev) => ({
      ...prev,
      [collection]: prev[collection].map((it) => (it.id === id ? { ...it, ...patch } : it)),
    }));
  }, [persist]);

  const removeItem = useCallback((collection, id) => {
    persist((prev) => ({ ...prev, [collection]: prev[collection].filter((it) => it.id !== id) }));
  }, [persist]);

  const updateCompany = useCallback((patch) => {
    persist((prev) => ({ ...prev, company: { ...prev.company, ...patch } }));
  }, [persist]);

  const renameVoucherField = useCallback((field, from, to) => {
    persist((prev) => ({
      ...prev,
      vouchers: prev.vouchers.map((v) => (v[field] === from ? { ...v, [field]: to } : v)),
    }));
  }, [persist]);
  const renameVoucherCategory = useCallback((from, to) => renameVoucherField('category', from, to), [renameVoucherField]);

  const getCustomer = useCallback((id) => state.customers.find((c) => c.id === id), [state.customers]);

  const getNextNumber = useCallback((prefix, collection) => buildNumber(state.company, collection, state[collection] || []), [state]);

  // Reference numbers: build the next one for a document type, and advance the manual
  // "next number" override (if the user set one in Company Settings) once it has been used.
  const generateNumber = useCallback(
    (type, dateISO) => buildNumber(state.company, type, state[type] || [], dateISO),
    [state],
  );

  const consumeNumber = useCallback((type, number) => {
    persist((prev) => {
      const manual = Number(prev.company.nextNumbers?.[type]);
      if (!manual) return prev;
      const used = seqOf(number);
      if (used < manual) return prev;
      return { ...prev, company: { ...prev.company, nextNumbers: { ...prev.company.nextNumbers, [type]: used + 1 } } };
    });
  }, [persist]);

  // Convert accepted quotation to invoice
  const convertQuotationToInvoice = useCallback((quotation) => {
    const today = new Date().toISOString().slice(0, 10);
    const number = buildNumber(state.company, 'invoices', state.invoices, today);
    const due = new Date(); due.setDate(due.getDate() + 30);
    const invoice = {
      id: genId(),
      number,
      customerId: quotation.customerId,
      date: today,
      dueDate: due.toISOString().slice(0, 10),
      status: 'unpaid',
      items: quotation.items,
      amountPaid: 0,
      fromQuotation: quotation.number,
    };
    persist((prev) => ({
      ...prev,
      invoices: [invoice, ...prev.invoices],
      quotations: prev.quotations.map((q) => (q.id === quotation.id ? { ...q, status: 'accepted' } : q)),
    }));
    return invoice;
  }, [persist, state.company, state.invoices]);

  // Applies a payment to an invoice (by number) and recalculates its status
  const recordPaymentOnInvoice = useCallback((invoiceNumber, amount) => {
    const paidAmount = Number(amount) || 0;
    if (!invoiceNumber || paidAmount <= 0) return;
    persist((prev) => ({
      ...prev,
      invoices: prev.invoices.map((inv) => {
        if (inv.number !== invoiceNumber) return inv;
        const { total } = computeTotals(inv.items, prev.company.sstRate);
        const paid = Math.min(Number(inv.amountPaid || 0) + paidAmount, total);
        const status = paid >= total - 0.005 ? 'paid' : paid > 0 ? 'partial' : inv.status;
        return { ...inv, amountPaid: paid, status };
      }),
    }));
  }, [persist]);

  const invoiceBalance = useCallback((invoiceNumber) => {
    const inv = state.invoices.find((i) => i.number === invoiceNumber);
    if (!inv) return 0;
    const { total } = computeTotals(inv.items, state.company.sstRate);
    return Math.max(0, total - Number(inv.amountPaid || 0));
  }, [state.invoices, state.company.sstRate]);

  const reset = useCallback(() => {
    resetState();
    const fresh = loadState();
    setState(fresh);
    return fresh;
  }, []);

  const value = {
    ...state,
    addItem, updateItem, removeItem, updateCompany,
    getCustomer, getNextNumber, convertQuotationToInvoice, reset,
    recordPaymentOnInvoice, invoiceBalance, generateNumber, consumeNumber, renameVoucherCategory, renameVoucherField,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
